import * as React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers-pro";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {Formik, Form} from "formik";
import * as Yup from "yup";
import {dateToString} from "../../Utils/dateUtils";

/**
 * Modal to edit an existing team-history transition.
 * For the current (open) team, only Start Date is editable — End Date stays
 * disabled. For a previous (closed) team period, both Start Date and End Date
 * are editable and required.
 */
const EditTeamTransitionModal = (props) => {
    const {open, onClose, entry, onSubmit} = props;

    if (!entry) {
        return null;
    }

    const isCurrent = !entry.end_date;

    const validationSchema = Yup.object({
        start_date: Yup.date()
            .nullable()
            .typeError("Invalid date")
            .required("Start date is required"),
        end_date: isCurrent
            ? Yup.date().nullable()
            : Yup.date()
                .nullable()
                .typeError("Invalid date")
                .required("End date is required")
                .min(Yup.ref("start_date"), "End date cannot be earlier than start date"),
    });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit Team Transition — {entry.team_name || `Team #${entry.team_id}`}</DialogTitle>
            <Formik
                initialValues={{
                    start_date: entry.start_date ? new Date(entry.start_date) : null,
                    end_date: entry.end_date ? new Date(entry.end_date) : null,
                }}
                validationSchema={validationSchema}
                onSubmit={(values, {setSubmitting}) => {
                    const payload = {
                        start_date: dateToString(new Date(values.start_date), "yyyy-MM-dd"),
                    };

                    if (!isCurrent) {
                        payload.end_date = dateToString(new Date(values.end_date), "yyyy-MM-dd");
                    }

                    onSubmit(entry.user_team_history_id, payload);
                    setSubmitting(false);
                    onClose();
                }}
            >
                {({values, errors, touched, isSubmitting, setFieldValue}) => (
                    <Form>
                        <DialogContent>
                            {isCurrent && (
                                <Typography variant="body2" sx={{color: "text.secondary", marginBottom: "16px"}}>
                                    This is the current team. End Date cannot be edited here — change the Team
                                    field to switch teams.
                                </Typography>
                            )}
                            <Grid container spacing={3}>
                                <Grid item xs={isCurrent ? 12 : 6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="Start Date*"
                                            inputFormat="dd/MM/yyyy"
                                            value={values.start_date}
                                            onChange={(newValue) => setFieldValue("start_date", newValue)}
                                            renderInput={(startProps) => (
                                                <TextField
                                                    {...startProps}
                                                    fullWidth
                                                    size="small"
                                                    error={touched.start_date && Boolean(errors.start_date)}
                                                    helperText={touched.start_date && errors.start_date}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                {!isCurrent && (
                                    <Grid item xs={6}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                label="End Date*"
                                                inputFormat="dd/MM/yyyy"
                                                value={values.end_date}
                                                onChange={(newValue) => setFieldValue("end_date", newValue)}
                                                renderInput={(endProps) => (
                                                    <TextField
                                                        {...endProps}
                                                        fullWidth
                                                        size="small"
                                                        error={touched.end_date && Boolean(errors.end_date)}
                                                        helperText={touched.end_date && errors.end_date}
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{padding: "16px 24px"}}>
                            <Button
                                type="button"
                                variant="contained"
                                color="secondary"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                color="primary"
                                loading={isSubmitting}
                            >
                                Save Changes
                            </LoadingButton>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default EditTeamTransitionModal;
